import { useMemo } from "react";

/** Lightweight SVG donut that renders one slice per status with a clickable
 *  segment + a center label. No charting library — just `circle` strokes
 *  with computed `stroke-dasharray`/`stroke-dashoffset`.
 *
 *  segments: [{ key, label, value, color }] — slice angle is value/total.
 *  onSegmentClick(key) fires when the user taps a slice. */
export default function StatusDonut({ segments = [], onSegmentClick, total: totalProp }) {
    const cx = 110, cy = 110, r = 72, c = 2 * Math.PI * r;
    const total = useMemo(
        () => totalProp ?? segments.reduce((acc, s) => acc + (s.value || 0), 0),
        [segments, totalProp],
    );

    let offset = 0;
    const slices = segments.map((s) => {
        const frac = total > 0 ? (s.value || 0) / total : 0;
        const len = c * frac;
        const node = (
            <circle
                key={s.key}
                cx={cx} cy={cy} r={r}
                fill="none"
                stroke={s.color || "#FF4400"}
                strokeWidth={total > 0 && frac > 0 ? 16 : 0}
                strokeDasharray={`${len} ${c - len}`}
                strokeDashoffset={-offset}
                style={{ cursor: onSegmentClick ? "pointer" : "default", transition: "stroke-width 220ms ease-out" }}
                onClick={() => onSegmentClick && onSegmentClick(s.key)}
                data-testid={`donut-slice-${s.key}`}
            >
                <title>{s.label}: {s.value}</title>
            </circle>
        );
        offset += len;
        return node;
    });

    return (
        <div className="flex items-center gap-6">
            <svg width="220" height="220" viewBox="0 0 220 220" data-testid="status-donut">
                {/* Track */}
                <circle cx={cx} cy={cy} r={r} fill="none"
                    stroke="rgba(82,82,91,0.18)" strokeWidth="16" />
                {/* Slices (rotated -90deg so 12 o'clock is the start) */}
                <g transform={`rotate(-90 ${cx} ${cy})`}>{slices}</g>
                {/* Center */}
                <text x={cx} y={cy - 4} textAnchor="middle"
                    fontSize="34" fontWeight="600" fill="#fafafa"
                    style={{ fontFamily: "Cabinet Grotesk, ui-sans-serif" }}>
                    {total}
                </text>
                <text x={cx} y={cy + 18} textAnchor="middle"
                    fontSize="10" fill="#a1a1aa" letterSpacing="2"
                    style={{ fontFamily: "JetBrains Mono, monospace", textTransform: "uppercase" }}>
                    Total
                </text>
            </svg>
            <ul className="space-y-2 text-sm">
                {segments.map(s => (
                    <li
                        key={s.key}
                        data-testid={`donut-legend-${s.key}`}
                        onClick={() => onSegmentClick && onSegmentClick(s.key)}
                        className={`flex items-center gap-3 ${onSegmentClick ? "cursor-pointer hover:text-white" : ""} text-zinc-400`}
                    >
                        <span className="w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} />
                        <span className="flex-1">{s.label}</span>
                        <span className="font-mono text-zinc-200 tabular-nums">{s.value}</span>
                    </li>
                ))}
                {total === 0 && (
                    <li className="text-zinc-600 text-xs">No projects yet.</li>
                )}
            </ul>
        </div>
    );
}
